import DatepickerControl from '@/components/controls/datepicker-control';
import SelectControl from '@/components/controls/select-control';
import { Heading } from '@/components/heading/Heading';
import { Loads } from '@/components/loads';
import { Button } from '@/components/ui/button';
import { Form } from '@/components/ui/form';
import usePartyGrainStore from '@/features/parties-grain/store/usePartyGrainStore';
import { cn } from '@/lib/utils';
import { CircleCheck, Download, RefreshCcw } from 'lucide-react';
import { FormProvider } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useAnalysisByGrain } from '../../hooks/useAnalysisByGrain';
import { generateGrainPDF } from '../general-grain';
import { SuffixInput } from './SuffixInput';

export const AnalysisGrainForm = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const {
    form,
    user,
    isSeed,
    onSubmit,
    loadGrain,
    toggleChip,
    selectAllChips,
    isDisabled,
    contractList,
    selectedChips,
    farmerContractId,
    farmerByTaxnumbers,
    grainAnalysisLoading,
  } = useAnalysisByGrain();

  const { classListByGrain } = usePartyGrainStore();

  return (
    <FormProvider {...form}>
      <Form {...form}>
        <div className="flex flex-col min-h-screen bg-gray-100 py-8 px-10">
          <Heading
            title={t('analysis.grain')}
            back
            onBack={() => navigate(-1)}
            className="mb-8"
          />

          <div className="flex items-start justify-between mb-6">
            <div className="flex items-center gap-6">
              <SelectControl
                name="farmer_contract_id"
                label={t('contract')}
                options={contractList}
                control={form.control}
                placeholder={t('choose.data')}
                className={cn('col-span-4 min-w-[250px]')}
                disabledItem={false}
              />
              <DatepickerControl
                control={form.control}
                name="selected_date"
                label={t('choose.date')}
                className="min-w-[250px]"
                inputClass="bg-white"
              />
            </div>

            <div className="flex flex-col items-end space-y-4">
              <Button
                variant="outline"
                className="bg-white border-gray-300 rounded-lg px-4 h-10"
                onClick={() =>
                  generateGrainPDF(
                    loadGrain!,
                    user!,
                    loadGrain?.number!,
                    loadGrain?.created_at!,
                    loadGrain?.farmer_tax_number!,
                  )
                }
              >
                <Download className="mr-2 text-[#47AA62]" size={18} />
                {t('download.3dqim')}
              </Button>

              {farmerContractId &&
                farmerByTaxnumbers &&
                farmerByTaxnumbers.length > 0 && (
                  <Loads
                    items={farmerByTaxnumbers}
                    selectedChips={selectedChips}
                    onToggle={toggleChip}
                    onSelectAll={selectAllChips}
                  />
                )}
            </div>
          </div>

          <div className="mb-6">
            <h2 className="text-lg font-medium">{t('informations')}</h2>
          </div>

          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8">
            <div className="flex flex-col gap-6">
              <div className="grid grid-cols-10 gap-x-6 gap-y-6">
                <SuffixInput
                  name="humidity"
                  label={t('humidity') as string}
                  suffix="%"
                  className="col-span-2"
                  isNumber
                  disabled={isDisabled}
                />

                <SuffixInput
                  name="pollution"
                  label={t('pollution') as string}
                  suffix="%"
                  className="col-span-2"
                  isNumber
                  disabled={isDisabled}
                />

                <SuffixInput
                  name="natura"
                  label={t('natura') as string}
                  suffix="gr"
                  className="col-span-2"
                  isNumber
                  disabled={isDisabled}
                />

                <SuffixInput
                  name="grain_mixture"
                  label={t('grain.mixture') as string}
                  suffix="%"
                  className="col-span-2"
                  isNumber
                  disabled={isDisabled}
                />

                <SuffixInput
                  name="kleykovina"
                  label={t('grain.gluten') as string}
                  suffix="%"
                  className="col-span-2"
                  isNumber
                  disabled={isDisabled}
                />

                {isSeed && (
                  <SuffixInput
                    name="prime_grain"
                    label={t('prime.grain') as string}
                    suffix="%"
                    className="col-span-2"
                    disabled={isDisabled}
                  />
                )}

                <SuffixInput
                  name="steklovidnost"
                  label={t('grain.vitreous') as string}
                  suffix="%"
                  className="col-span-2"
                  isNumber
                  disabled={isDisabled}
                />

                <SelectControl
                  name="grain_class"
                  label={t('grain.class') as string}
                  control={form.control}
                  options={classListByGrain}
                  className="col-span-2"
                  disabled={isDisabled}
                />
              </div>

              <div className="flex items-center justify-end gap-3 pt-4 ">
                <span className="font-medium text-black mr-10">
                  {t('total.weight')}:{' '}
                  <span className="text-green-600">
                    {selectedChips?.reduce((acc, curr) => {
                      if (!curr.weight) return acc;
                      return acc + Number(curr.weight);
                    }, 0)}{' '}
                    kg
                  </span>
                </span>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => form.reset()}
                  className="rounded-lg border-gray-300 h-10 px-6 text-gray-700"
                  disabled={isDisabled}
                >
                  <RefreshCcw className="mr-2 text-orange-500" size={18} />
                  {t('reset')}
                </Button>

                <Button
                  type="submit"
                  className="bg-[#47AA62] hover:bg-[#3d9154] text-white rounded-lg h-10 px-8 border-0"
                  disabled={isDisabled}
                  isLoading={grainAnalysisLoading}
                >
                  <CircleCheck color="white" size={18} />
                  {t('save')}
                </Button>
              </div>
            </div>
          </form>
        </div>
      </Form>
    </FormProvider>
  );
};
